import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fmtDataISO } from "@atlas/ui/web";
import type { BancoApiTokenMeta } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { formatBackendErr } from "../../lib/format-backend-err";
import { ApiDocsExplorer, type TryTokenInfo } from "../../components/ApiDocsExplorer";
import { DocsShell } from "../../components/DocsShell";
import { RegrasConvenio } from "./regras-convenio";
import { layersDe } from "../../lib/api-docs-layers";

/**
 * Documentacao de integracao do banco: guias, referencia e credenciais.
 *
 * Antes esta tela era so o console. A documentacao narrativa vivia num site
 * Docusaurus publico, que foi aposentado — regra de convenio e posicao comercial
 * entre averbadora, prefeitura e banco, e num site aberto um banco leria as
 * condicoes do outro no mesmo convenio.
 *
 * As credenciais continuam embutidas na barra de autorizacao do console (e nao
 * numa secao propria): a tela e sobre integrar, e quem ocupa o espaco tem que ser
 * a lista de endpoints.
 */
export function BancoApi() {
  const { slug } = useParams<{ slug?: string }>();
  const nav = useNavigate();

  const q = useQuery({
    // Prefixo "banco" obrigatorio: o layout invalida ["banco"] na troca de
    // convenio e o overlay observa essas queries.
    queryKey: ["banco", "api-tokens"],
    queryFn: () => atlas.banco.apiTokens(),
    staleTime: 60_000,
  });

  const ambientesQ = useQuery({
    queryKey: ["banco", "api-ambientes"],
    queryFn: () => atlas.banco.apiAmbientes(),
    staleTime: 60_000,
  });
  const servers = ambientesQ.data
    ? [
        { key: "sandbox", label: "Homologação", url: ambientesQ.data.sandboxUrl },
        { key: "production", label: "Produção", url: ambientesQ.data.productionUrl },
      ]
    : undefined;

  const [info, setInfo] = useState<TryTokenInfo>({ estado: "vazio" });
  // Ambiente do servidor selecionado no console. A chave do server ("sandbox" /
  // "production") e' o mesmo vocabulario do `environment` do token, entao serve
  // de filtro direto. Default "sandbox": e' o que o seletor abre.
  const [ambiente, setAmbiente] = useState<string>("sandbox");
  const layers = useMemo(() => layersDe("banco"), []);
  const tokens = q.data?.tokens ?? [];
  // A API recusa chave de um ambiente no outro, entao listar as duas juntas so
  // induziria a colar a errada e tomar 401.
  const tokensDoAmbiente = tokens.filter((t) => t.environment === ambiente);
  const rotuloAmbiente = ambiente === "production" ? "produção" : "homologação";
  const rotuloOutro = ambiente === "production" ? "homologação" : "produção";

  // Dono do token independe do ambiente: uma chave de produção autorizada com
  // "Homologação" aberto continua sendo deste banco, só não aparece na lista.
  const tokenDeOutroDono =
    info.estado === "ok" &&
    info.camada === "banco" &&
    !!info.tokenId &&
    tokens.length > 0 &&
    !tokens.some((t) => t.id === info.tokenId);

  const resumo = q.isPending
    ? "carregando credenciais…"
    : q.isError
      ? "não foi possível consultar suas credenciais"
      : tokenDeOutroDono
        ? "⚠ o token autorizado não é uma credencial desta consignatária"
        : tokensDoAmbiente.length === 0
          ? tokens.length === 0
            ? "nenhuma credencial emitida — solicite à averbadora"
            : `nenhuma credencial de ${rotuloAmbiente} — você só tem de ${rotuloOutro}`
          : `${tokensDoAmbiente.length} ${tokensDoAmbiente.length === 1 ? "credencial" : "credenciais"} de ${rotuloAmbiente}`;

  return (
    <DocsShell
      titulo="Integração via API"
      subtitulo="As mesmas operações do portal, disponíveis para o seu sistema em /v1/external/banco/*."
      slug={slug}
      onNavegar={(s) => nav(`/banco/api/${s}`)}
      extras={[{
        slug: "convenios",
        titulo: "Regras dos seus convênios",
        resumo: "Tetos e exigências por órgão",
        node: <RegrasConvenio />,
      }]}
      referencia={
        <ApiDocsExplorer
          layers={layers}
          linkTokens={null}
          storageKey="atlas:try-token:banco"
          onTokenInfoChange={setInfo}
          servers={servers}
          onServerChange={setAmbiente}
          credenciais={{
            resumo,
            detalhe: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {q.isPending ? (
                  <p style={{ margin: 0, padding: "12px 16px", fontSize: 13, color: "var(--text-muted)" }}>Carregando…</p>
                ) : q.isError ? (
                  <p style={{ margin: 0, padding: "12px 16px", fontSize: 13, color: "var(--danger-500)" }}>
                    {formatBackendErr(q.error, "Falha ao consultar as credenciais.")}
                  </p>
                ) : tokensDoAmbiente.length === 0 ? (
                  <p style={{ margin: 0, padding: "12px 16px", fontSize: 13, color: "var(--text-muted)" }}>
                    {tokens.length === 0 ? (
                      <>
                        A emissão é feita pela averbadora. Ao solicitar, informe o ambiente
                        (<code>sandbox</code> para homologar, <code>production</code> para operar) e quais operações
                        você precisa — a lista de endpoints serve de referência para esse pedido.
                      </>
                    ) : (
                      <>
                        Você não tem credencial de <b>{rotuloAmbiente}</b> — só de {rotuloOutro}. Troque o servidor
                        no topo do console, ou solicite uma chave deste ambiente à averbadora.
                      </>
                    )}
                  </p>
                ) : (
                  tokensDoAmbiente.map((t) => (
                    <LinhaCredencial key={t.id} token={t} emUso={info.estado === "ok" && info.tokenId === t.id} />
                  ))
                )}
                <p style={{ margin: 0, padding: "10px 16px", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--text-muted)" }}>
                  Só aparecem as credenciais do servidor selecionado: a API recusa uma chave de homologação em
                  produção, e vice-versa. A de <b>homologação</b> fica visível aqui — é ambiente sem dado real.
                  A de <b>produção</b> aparece uma única vez, na emissão: guardamos apenas o hash SHA-256 dela,
                  então nem a averbadora consegue recuperá-la. Se perdeu, solicite uma credencial nova.
                </p>
              </div>
            ),
          }}
        />
      }
    />
  );
}

/** Ambiente · chave · status. Nada mais: quais endpoints a credencial alcanca e
 *  decisao da averbadora na emissao, nao informacao acionavel pro banco aqui. */
function LinhaCredencial({ token, emUso }: { token: BancoApiTokenMeta; emUso: boolean }) {
  const [copiado, setCopiado] = useState(false);
  const producao = token.environment === "production";
  // Chave inteira em sandbox; em producao so o prefixo existe.
  const exibido = token.token ?? token.prefix;

  async function copiar() {
    if (!token.token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      /* clipboard bloqueado pelo navegador — o texto continua selecionavel */
    }
  }

  return (
    <div style={{
      borderTop: "1px solid var(--border)",
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: "10px 16px",
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, minWidth: 68, textAlign: "center",
        padding: "2px 8px", borderRadius: 4,
        color: producao ? "var(--gold-500)" : "var(--text-muted)",
        border: `1px solid ${producao ? "var(--gold-500)" : "var(--border-strong)"}`,
      }}>
        {producao ? "produção" : "sandbox"}
      </span>

      <code style={{
        fontFamily: "var(--font-mono)", fontSize: 12, flex: 1, minWidth: 240,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        color: token.token ? "var(--text)" : "var(--text-muted)",
      }}>
        {exibido}
      </code>

      {token.token ? (
        <button
          type="button"
          onClick={copiar}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--accent)" }}
        >
          {copiado ? "copiado ✓" : "copiar"}
        </button>
      ) : null}

      {emUso ? <span style={{ fontSize: 11, color: "var(--emerald-500)", whiteSpace: "nowrap" }}>● em uso</span> : null}

      <span style={{ fontSize: 12, color: token.ativo ? "var(--emerald-500)" : "var(--text-dim)", whiteSpace: "nowrap" }}>
        {token.ativo ? "Ativo" : token.bancoInativo ? "Consignatária pausada" : "Pausado"}
      </span>

      <span style={{ fontSize: 11, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
        {fmtDataISO(token.createdAt)}
      </span>
    </div>
  );
}
